import React from 'react'

// Informal German second-person pronouns. In lang.js these are written
// capitalised (orgaMAX style). For MeinBüro they must be lowercased when they
// appear mid-sentence, but stay capitalised at the start of a sentence (where
// every German word is capitalised anyway).
const PRONOUNS = ['Du', 'Dich', 'Dir', 'Dein', 'Deine', 'Deinem', 'Deinen', 'Deiner', 'Deines']

// Word boundaries (\b) prevent false positives such as "Durch", "Duplizieren",
// "Direkt" or "Direktlink": there a letter follows "Du"/"Dir", so no boundary
// matches and the word is left untouched.
const PRONOUN_REGEX = new RegExp(`\\b(?:${PRONOUNS.join('|')})\\b`, 'g')

// Inline formatting tags are "seen through" when looking back for a sentence
// boundary: a pronoun wrapped in or following them is still mid-sentence
// (e.g. "… aus <a ...>Deiner Historie</a>" -> "deiner").
const TRAILING_INLINE_TAGS = /(\s*<\/?(?:a|b|i|u|s|em|strong|span|small|mark)\b[^>]*>)+\s*$/i

// A pronoun keeps its capital letter when the preceding text marks a new
// sentence/line/quote:
//  - a line break or block-level tag (<br>, </br>, <p>, <li>, <div>, <h1>…),
//  - an opening bracket or quote ("(Du …", '"Dein …'),
//  - sentence-ending punctuation, including a colon, optionally followed by a
//    closing quote/bracket.
// After a comma the pronoun is treated as mid-sentence and is lowercased.
const LINE_BREAK_OR_BLOCK = /(?:\n|<\s*\/?\s*br\s*\/?\s*>|<\/?(?:p|div|li|ul|ol|h[1-6])\b[^>]*>)\s*$/i
const OPENING_PUNCTUATION = /[([{«»„“‚‘"']\s*$/
const SENTENCE_END = /[.!?…:]+["'»“)\]]?\s*$/

/**
 * Lowercase informal pronouns that appear mid-sentence inside a single string.
 * Sentence-start occurrences and already-lowercase words are left unchanged.
 */
export const lowercasePronounsMidSentence = (text) => {
  if (typeof text !== 'string' || !text) return text

  return text.replace(PRONOUN_REGEX, (match, offset, full) => {
    // Look back at the text before the pronoun, seeing through inline tags.
    const before = full.slice(0, offset).replace(TRAILING_INLINE_TAGS, '')
    const isSentenceStart
      = before.trim() === ''
      || LINE_BREAK_OR_BLOCK.test(before)
      || OPENING_PUNCTUATION.test(before)
      || SENTENCE_END.test(before)
    return isSentenceStart ? match : match.charAt(0).toLowerCase() + match.slice(1)
  })
}

/**
 * Recursively transform a lang value tree so that informal pronouns are
 * lowercased mid-sentence. Handles plain strings, nested plain objects and
 * arrays, and wraps functions so their string return values are transformed
 * too. React elements and any other value types are returned untouched.
 */
export const lowercasePronounsDeep = (value) => {
  if (typeof value === 'string') return lowercasePronounsMidSentence(value)

  if (typeof value === 'function') {
    return (...args) => lowercasePronounsDeep(value(...args))
  }

  if (Array.isArray(value)) return value.map(lowercasePronounsDeep)

  if (React.isValidElement(value)) return value

  if (isPlainObject(value)) {
    const result = {}
    for (const key of Object.keys(value)) {
      result[key] = lowercasePronounsDeep(value[key])
    }
    return result
  }

  return value
}

const isPlainObject = (value) => {
  if (value === null || typeof value !== 'object') return false
  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}
